import React, { useMemo } from 'react';
import { Modal } from '@mantine/core';
import { IconCheck, IconPlayerPlay } from '@tabler/icons-react';

import classes from './DayActivityModal.module.css';

export function ymd(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseDateOnly(value) {
  if (!value) return null;
  const raw = String(value).slice(0, 10);
  const [year, month, day] = raw.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

export function isOverlay(entry) {
  return entry?.entry_kind === 'overlay';
}

export function isClosure(entry) {
  return entry?.entry_kind === 'closure';
}

export function entryDisplayDate(entry) {
  if (isOverlay(entry) || isClosure(entry)) return parseDateOnly(entry.on_date);
  return parseDateOnly(entry.completed_on) || parseDateOnly(entry.recommended_on);
}

export function isRestDay(slot) {
  return slot.day_kind_formatted === 'mandatory_rest';
}

export function isPendingLift(slot) {
  if (isOverlay(slot) || isClosure(slot)) return false;
  const status = slot.status_formatted || '';
  return (
    status === 'pending' &&
    Boolean(slot.workout_id) &&
    !isRestDay(slot)
  );
}

export function isCompletedOverlay(entry) {
  if (!isOverlay(entry)) return false;
  return (
    entry.session_status_formatted === 'completed' ||
    Number(entry.session_status) === 2
  );
}

export function isLoadableOverlay(entry, isToday) {
  return (
    isToday &&
    isOverlay(entry) &&
    Boolean(entry.workout_id || entry.workout?.id) &&
    !isCompletedOverlay(entry)
  );
}

export function entriesForDay(entries, dateKey) {
  return (entries || [])
    .filter((entry) => {
      const date = entryDisplayDate(entry);
      return date && ymd(date) === dateKey;
    })
    .sort((a, b) => {
      const rank = (entry) => {
        if (isClosure(entry)) return 0;
        if (isOverlay(entry)) return 2;
        return 1;
      };
      const delta = rank(a) - rank(b);
      if (delta !== 0) return delta;
      return (a.queue_position || 0) - (b.queue_position || 0);
    });
}

export default function DayActivityModal({
  opened,
  onClose,
  dateLabel,
  entries = [],
  loading = false,
  isToday = false,
  onLoadWorkout,
}) {
  const pendingTodayLift = useMemo(() => {
    if (!isToday) return null;
    return entries.find((entry) => isPendingLift(entry)) || null;
  }, [entries, isToday]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Day activity"
      centered
      radius="md"
      styles={{
        content: {
          background: '#fffdf8',
          color: 'var(--brand-ink)',
        },
        header: {
          background: '#fffdf8',
        },
        title: {
          fontWeight: 800,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontSize: '0.82rem',
        },
      }}
    >
      <div className={classes.modalBody}>
        {dateLabel ? <h2 className={classes.dateLabel}>{dateLabel}</h2> : null}
        {loading ? (
          <p className={classes.empty}>Loading…</p>
        ) : entries.length === 0 ? (
          <p className={classes.empty}>Nothing scheduled for this day.</p>
        ) : (
          <ul className={classes.slotList}>
            {entries.map((entry) => {
              if (isClosure(entry)) {
                return (
                  <li key={`closure-${entry.id}-${entry.on_date}`}>
                    <div className={`${classes.slotRow} ${classes.slotRowOverlay}`}>
                      <span
                        className={`${classes.slotMark} ${classes.slotOverlay}`}
                      />
                      <span className={classes.slotBody}>
                        <span className={classes.slotName}>
                          {entry.name || 'Gym closed'}
                        </span>
                        <span className={classes.slotMeta}>
                          {entry.blocks_schedule
                            ? 'Gym closed · blocks schedule'
                            : 'Gym closed · stacks with training'}
                        </span>
                      </span>
                    </div>
                  </li>
                );
              }

              if (isOverlay(entry)) {
                const name =
                  entry.name || entry.workout?.name || 'Extra session';
                const done = isCompletedOverlay(entry);
                const canLoad = isLoadableOverlay(entry, isToday);
                const RowTag = canLoad ? 'button' : 'div';
                return (
                  <li key={`overlay-${entry.id}-${entry.on_date}`}>
                    <RowTag
                      type={canLoad ? 'button' : undefined}
                      className={`${classes.slotRow} ${classes.slotRowOverlay}${
                        canLoad ? ` ${classes.slotRowAction}` : ''
                      }`}
                      onClick={
                        canLoad
                          ? () =>
                              onLoadWorkout?.({
                                source: 'overlay',
                                id: entry.id,
                                onDate: entry.on_date,
                              })
                          : undefined
                      }
                    >
                      <span
                        className={[
                          classes.slotMark,
                          classes.slotOverlay,
                          done ? classes.slotDone : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {done ? <IconCheck size={12} stroke={3} /> : null}
                      </span>
                      <span className={classes.slotBody}>
                        <span className={classes.slotName}>{name}</span>
                        <span className={classes.slotMeta}>
                          {done
                            ? 'Overlay · completed'
                            : entry.blocks_schedule
                              ? 'Overlay · blocks schedule'
                              : 'Overlay · stacks with training'}
                          {entry.workout?.name
                            ? ` · ${entry.workout.name}`
                            : ''}
                        </span>
                      </span>
                      {canLoad ? (
                        <IconPlayerPlay size={18} stroke={2} />
                      ) : null}
                    </RowTag>
                  </li>
                );
              }

              const canLoad =
                isToday && pendingTodayLift?.id === entry.id;
              const done = entry.status_formatted === 'completed';
              const pending = entry.status_formatted === 'pending';
              const name =
                entry.workout?.name ||
                (isRestDay(entry) ? 'Rest' : 'Session');
              const RowTag = canLoad ? 'button' : 'div';
              return (
                <li key={entry.id}>
                  <RowTag
                    type={canLoad ? 'button' : undefined}
                    className={`${classes.slotRow}${
                      canLoad ? ` ${classes.slotRowAction}` : ''
                    }`}
                    onClick={
                      canLoad
                        ? () =>
                            onLoadWorkout?.({
                              source: 'slot',
                              id: entry.id,
                            })
                        : undefined
                    }
                  >
                    <span
                      className={[
                        classes.slotMark,
                        done ? classes.slotDone : '',
                        pending && !done ? classes.slotPending : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {done ? <IconCheck size={12} stroke={3} /> : null}
                    </span>
                    <span className={classes.slotBody}>
                      <span className={classes.slotName}>{name}</span>
                      <span className={classes.slotMeta}>
                        {entry.status_formatted || 'scheduled'}
                        {entry.day_kind_formatted
                          ? ` · ${entry.day_kind_formatted}`
                          : ''}
                      </span>
                    </span>
                    {canLoad ? (
                      <IconPlayerPlay size={18} stroke={2} />
                    ) : null}
                  </RowTag>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
